'use client'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Course } from '@/payload-types'
import { useAuth } from '@/providers/Auth'
import { formatPriceFromEuros } from '@/utilities/formatPriceFromEuros'
import { usePostHog } from 'posthog-js/react'
import { useState } from 'react'
import { toast } from 'sonner'
import { ShieldCheck, Zap, CreditCard, Loader2, Calendar } from 'lucide-react'

const INTAKE_CONSULT_PRICE_EUR = 144

interface CoursePurchaseButtonProps {
  course: Course
  className?: string
  bare?: boolean
}

export const CoursePurchaseButton: React.FC<CoursePurchaseButtonProps> = ({
  course,
  className,
  bare = false,
}) => {
  const { user } = useAuth()
  const posthog = usePostHog()
  const [isLoading, setIsLoading] = useState(false)
  const [includeIntakeConsult, setIncludeIntakeConsult] = useState(false)

  const handlePurchase = async () => {
    setIsLoading(true)

    posthog?.capture('begin_checkout', {
      course_id: course.id,
      course_slug: course.slug,
      course_title: course.title,
      price_eur: includeIntakeConsult
        ? course.priceEuro + INTAKE_CONSULT_PRICE_EUR
        : course.priceEuro,
      include_intake_consult: includeIntakeConsult,
      logged_in: Boolean(user),
    })

    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courseId: course.id,
          includeIntakeConsult,
          // Lets the Stripe webhook attribute the purchase event to this
          // browser person/session in PostHog
          posthogDistinctId: posthog?.get_distinct_id(),
          posthogSessionId: posthog?.get_session_id(),
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error(data?.error || 'Failed to create checkout session')
      }

      const { url } = await response.json()
      window.location.href = url
    } catch (error) {
      console.error('Error creating checkout session:', error)
      posthog?.captureException(error)
      toast.error(error instanceof Error ? error.message : 'Er ging iets mis bij het starten van de betaling.')
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <div className={bare ? undefined : 'overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-b from-card to-card/80 shadow-lg'}>
        {/* Header */}
        <div className={bare ? 'pb-4' : 'border-b border-border/50 bg-primary/5 px-6 py-4'}>
          <h3 className="text-lg font-bold text-foreground">Koop deze cursus</h3>
        </div>

        {/* Content */}
        <form
          className={bare ? 'space-y-4' : 'p-6 space-y-4'}
          onSubmit={(e) => {
            e.preventDefault()
            void handlePurchase()
          }}
        >
          {course.summary && (
            <p className="text-sm leading-relaxed text-muted-foreground">{course.summary}</p>
          )}

          {/* Features */}
          <ul className="space-y-3">
            <li className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Zap className="h-4 w-4 text-primary" />
              </div>
              <span>Direct toegang na betaling</span>
            </li>
            <li className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
              <span>Levenslange toegang tot materiaal</span>
            </li>
          </ul>

          {/* Price */}
          <div className="flex flex-col items-end">
            {course.priceFrom != null && (
              <span className="text-sm font-medium text-muted-foreground line-through">
                {formatPriceFromEuros(
                  includeIntakeConsult
                    ? course.priceFrom + INTAKE_CONSULT_PRICE_EUR
                    : course.priceFrom,
                )}
              </span>
            )}
            <span className="text-2xl font-bold text-primary">
              {includeIntakeConsult
                ? formatPriceFromEuros(course.priceEuro + INTAKE_CONSULT_PRICE_EUR)
                : formatPriceFromEuros(course.priceEuro)}
            </span>
          </div>

          {/* Intake consult add-on */}
          <label className="relative flex cursor-pointer gap-3 overflow-hidden rounded-xl border border-border/50 p-4 pb-8 transition-colors hover:bg-muted/40 has-[:checked]:border-primary/40 has-[:checked]:bg-primary/5">
            <Calendar className="absolute right-3 top-3 h-4 w-4 text-primary/40" />
            <Checkbox
              id="intake-consult"
              checked={includeIntakeConsult}
              onCheckedChange={(checked) => setIncludeIntakeConsult(checked === true)}
              className="mt-0.5 shrink-0"
            />
            <div className="min-w-0 flex-1 pb-4">
              <p className="text-md font-medium text-foreground">Intake consult toevoegen</p>
              <p className="mt-1 text-sm text-muted-foreground">Online of op locatie in Soest. Ongeveer 70% wordt vergoed vanuit de basisverzekering</p>
            </div>
            <span className="absolute bottom-3 right-4 text-md font-semibold text-primary">{formatPriceFromEuros(INTAKE_CONSULT_PRICE_EUR)}</span>
          </label>

          {/* CTA Button */}
          <Button
            type="submit"
            disabled={isLoading || !course.stripeID}
            className="w-full gap-2 rounded-xl py-6 text-base font-semibold shadow-md shadow-primary/20 transition-all hover:shadow-lg hover:shadow-primary/25"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Bezig met laden...
              </>
            ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  Koop nu
                </>
              )}
            </Button>

          {/* Trust badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Veilige betaling via Stripe</span>
          </div>
        </form>
      </div>
    </div>
  )
}
